import React, { useState } from 'react';
import { Fuel, Zap } from 'lucide-react';
import { computeAnnualFuelCost, FUEL_TYPES } from '../../lib/fuelCost';
import useFuelPrices from '../../hooks/useFuelPrices';

/**
 * Spritkosten-Kachel — dunkelgrünes „Für mehr Auto"-Design.
 * Kraftstoff-Auswahl (Benzin/Diesel/Elektro), Live-Ø-Preise über Tankerkönig,
 * Literpreis-Vergleich „heute vs. effektiv mit FuelX".
 */

const fmt2 = (n) => n.toFixed(2).replace('.', ',');
const fmtEur = (n) => `${Math.round(n).toLocaleString('de-DE')} €`;

export default function AnnualFuelCostCard({
  mode = 'private',
  vehicleCount = 1,
  annualKm = 15000,
  variant = 'light',
  testId = 'annual-fuel-cost',
}) {
  const isPvt = mode === 'private';
  const compact = variant === 'compact';
  const [fuelType, setFuelType] = useState('petrol');
  const prices = useFuelPrices();

  const ft = FUEL_TYPES[fuelType];
  const isElectric = fuelType === 'electric';
  const isLive = !isElectric && prices?.source === 'tankerkoenig';
  const livePrice = prices ? prices[ft.priceKey] : null;
  const price = livePrice != null ? livePrice : ft.fallbackPrice;

  const { totalEur, perVehicleEur, monthlyEur, effectivePrice } = computeAnnualFuelCost({ vehicleCount, annualKm, fuelType, pricePerUnit: price });
  const showBreakdown = vehicleCount > 1;
  const effectivePct = Math.max(Math.min((effectivePrice / price) * 100, 100), 20);

  const scopeLabel = isPvt
    ? `${vehicleCount} ${vehicleCount === 1 ? 'Fahrzeug' : 'Fahrzeuge'} im Haushalt · ${annualKm.toLocaleString('de-DE')} km/Jahr · Ø ${String(ft.per100).replace('.', ',')} ${ft.unit}/100 km`
    : `${vehicleCount} ${vehicleCount === 1 ? 'Firmenfahrzeug' : 'Firmenfahrzeuge'} · ${annualKm.toLocaleString('de-DE')} km/Fzg/Jahr · Ø ${String(ft.per100).replace('.', ',')} ${ft.unit}/100 km`;

  const Icon = isElectric ? Zap : Fuel;

  return (
    <div
      className={`relative overflow-hidden ${compact ? 'rounded-xl' : 'rounded-2xl'} ring-1 ring-emerald-300/25`}
      style={{ background: 'linear-gradient(135deg, #022c22 0%, #064e3b 58%, #047857 135%)' }}
      data-testid={testId}
    >
      {/* Glows + Akzentlinien (wie „Für mehr Auto") */}
      <div aria-hidden className="absolute -top-16 -right-14 w-56 h-56 rounded-full bg-emerald-400/20 blur-3xl pointer-events-none" />
      <div aria-hidden className="absolute -bottom-20 -left-14 w-48 h-48 rounded-full bg-teal-300/10 blur-2xl pointer-events-none" />
      <div aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-emerald-300/70 to-transparent" />
      <div aria-hidden className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-emerald-300/40 to-transparent" />

      <div className={`relative ${compact ? 'p-3.5' : 'p-4 sm:p-5'}`}>
        {/* Kopf: Label + Marken-Logos (farbig) */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <p className={`flex items-center gap-2 font-semibold uppercase tracking-[0.22em] text-emerald-300 ${compact ? 'text-[9.5px]' : 'text-[10px]'}`} data-testid={`${testId}-label`}>
            <Icon className="w-3.5 h-3.5" strokeWidth={2.2} />
            Deine {isElectric ? 'Energiekosten' : 'Spritkosten'} pro Jahr
          </p>
          <div className="flex items-center gap-1" data-testid={`${testId}-brand-logos`}>
            {[
              { slug: 'shell', name: 'Shell' },
              { slug: 'aral', name: 'Aral' },
              { slug: 'bp', name: 'BP' },
            ].map((b) => (
              <span key={b.slug} className="inline-flex items-center justify-center h-[22px] px-1.5 rounded-md bg-white/95 shadow-sm" title={b.name}>
                <img src={`/img/brands/${b.slug}.svg`} alt={b.name} loading="lazy" className="h-3.5 w-auto object-contain" />
              </span>
            ))}
          </div>
        </div>
        {/* Kraftstoff-Auswahl */}
        <div className="flex justify-end mb-3">
          <div className="flex gap-1" data-testid={`${testId}-fuel-toggle`}>
            {Object.values(FUEL_TYPES).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setFuelType(t.key)}
                className={`px-2.5 py-1 rounded-full text-[9.5px] font-semibold uppercase tracking-[0.1em] transition-colors ${fuelType === t.key ? 'bg-white text-emerald-900 shadow-sm' : 'bg-white/10 text-emerald-100/80 hover:bg-white/20'}`}
                data-testid={`${testId}-fuel-${t.key}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Betrag + Rahmendaten */}
        <div className="flex items-end justify-between gap-3 flex-wrap mb-1">
          <p className={`font-display text-white tabular-nums leading-none ${compact ? 'text-2xl' : 'text-3xl'}`} data-testid={`${testId}-total`}>
            {fmtEur(totalEur)}
            <span className="text-xs font-normal text-emerald-100/60 ml-1.5">/ Jahr</span>
          </p>
          {/* Live-Preis */}
          <p className="flex items-center gap-1.5 text-[10px] text-emerald-100/80" data-testid={`${testId}-live-price`}>
            {isLive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
            Ø-Preis aktuell: <strong className="text-white tabular-nums">{fmt2(price)} €/{ft.unit}</strong>
            <span className="text-emerald-100/50">· {isLive ? 'Tankerkönig live' : 'Richtwert'}</span>
          </p>
        </div>
        <p className="text-[10px] text-emerald-100/60 leading-snug mb-3.5">{scopeLabel}</p>
        {showBreakdown && (
          <p className="text-[10px] text-emerald-100/50 -mt-2.5 mb-3.5 tabular-nums" data-testid={`${testId}-breakdown`}>
            ≈ {fmtEur(perVehicleEur)} je Fahrzeug · ≈ {fmtEur(monthlyEur)} / Monat
          </p>
        )}

        {/* Preis-Vergleich: heute vs. effektiv mit FuelX */}
        <div className="space-y-1.5" data-testid={`${testId}-liter-compare`}>
          <div className="flex items-center gap-2">
            <span className="w-[76px] sm:w-[96px] flex-shrink-0 text-[9.5px] font-medium text-emerald-100/70 leading-tight">{ft.pumpLabel}</span>
            <div className="flex-1 relative h-4 rounded-full overflow-hidden bg-white/10">
              <div className="absolute inset-y-0 left-0 w-full rounded-full bg-gradient-to-r from-rose-400 to-rose-500" />
              <span className="absolute inset-y-0 right-2 flex items-center text-[9px] font-bold text-white tabular-nums leading-none whitespace-nowrap">{fmt2(price)} €/{ft.unit}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-[76px] sm:w-[96px] flex-shrink-0 text-[9.5px] font-bold text-emerald-300 leading-tight">Effektiv mit FuelX*</span>
            <div className="flex-1 relative h-4 rounded-full overflow-hidden bg-white/10">
              <div className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500" style={{ width: `${effectivePct}%` }} />
              <span className="absolute inset-y-0 right-2 flex items-center text-[9px] font-bold text-white tabular-nums leading-none whitespace-nowrap drop-shadow-[0_1px_2px_rgba(2,44,34,0.6)]">{fmt2(effectivePrice)} €/{ft.unit}</span>
            </div>
            <span className="flex-shrink-0 text-[9px] font-bold tabular-nums px-1.5 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300 border border-emerald-400/25 whitespace-nowrap" data-testid={`${testId}-savings-badge`}>
              −{fmt2(ft.savings)} €/{ft.unit}
            </span>
          </div>
        </div>

        <p className="text-[8.5px] text-emerald-100/40 mt-2.5 leading-snug">
          *Ø-Ersparnis {fmt2(ft.savings)} €/{ft.unit} bei aktiver Nutzung (FuelGood + Hotel-Rückerstattungen).{' '}
          {isLive ? 'Preise: Tankerkönig / MTS-K (CC BY 4.0), bundesweiter Ø.' : 'Preis-Richtwert, keine Live-Daten.'}
        </p>
      </div>
    </div>
  );
}
